import { Modal } from "@xometry/xometry_loft";
import { useState, useMemo, useEffect } from "react";
import { useBulkUpload } from "./CustomerRFQ.context";
import { AnonymousUserFormWrapper, FormModalCss } from "./CustomerRFQ.style";
import { FormStepperFileUpload } from "./CustomerRFQFileUpload";
import FormStepper from "../FormStepper";
import { SuccessStep } from "./CustomerRFQSuccess";
import { Login } from "./CustomerRFQAnon/CustomerRFQLogin";
import { UserRegistrationForm } from "./CustomerRFQAnon/CustomerRFQRegister";
import { SubmissionData } from "../FormStepper/FormStepper";
import { useClient } from "../../apollo";
import {
  BulkUploadFileValidationQuery,
  BulkUploadFileValidationQueryResult,
  BulkUploadFileValidationQueryVariables,
  CustomerQuoteRequestFormMutationResult,
  CustomerQuoteRequestFormMutationVariables,
  CustomerQuoteRequestFormQuery,
} from "../../schema/generated/schema";
import { BulkUploadData } from "./CustomerRFQ.types";
import { useConfig } from "../proposals/contexts/configContext";

export const AnonymousUserFlow = ({ onSuccess }: { onSuccess: () => void }) => {
  const [registrationVisible, setRegistrationVisible] = useState(true);
  return (
    <AnonymousUserFormWrapper>
      {registrationVisible ? (
        <UserRegistrationForm
          isOpen={registrationVisible}
          onClose={() => setRegistrationVisible(false)}
          onSuccess={onSuccess}
          switchToLoginForm={() => setRegistrationVisible(false)}
        />
      ) : (
        <Login
          isOpen={!registrationVisible}
          onClose={() => setRegistrationVisible(true)}
          onSuccess={onSuccess}
          switchToRegistration={() => setRegistrationVisible(true)}
        />
      )}
    </AnonymousUserFormWrapper>
  );
};

export const FormStepperUploadWithAnonymousUserFlow = ({
  onSuccess,
  userEmail,
}: {
  onSuccess: () => void;
  userEmail: string | null | undefined;
}) => {
  if (userEmail) {
    return <FormStepperFileUpload />;
  }
  return <AnonymousUserFlow onSuccess={onSuccess} />;
};

export interface CustomerRFQFormProps {
  isOpen: boolean;
  handleClose: () => void;
  userEmail: string | null | undefined;
  refetchUser: () => void;
}

export const CustomerRFQForm = (props: CustomerRFQFormProps) => {
  const { isOpen, handleClose, userEmail, refetchUser } = props;
  const client = useClient();
  const { onBehalfOfUser } = useConfig();

  const {
    data: { files },
    actions: { processFiles, clearFiles },
  } = useBulkUpload();

  const [disableFileUploadNext, setDisableFileUploadNext] = useState(true);
  const [hasValidExtensions, setHasValidExtensions] = useState(false);
  const [disableFileUploadNextMessage, setDisableFileUploadNextMessage] =
    useState("");
  const isMissingFiles = useMemo(() => files.length === 0, [files.length]);

  useEffect(() => {
    if (isMissingFiles) {
      return;
    }

    const partFiles = files.map(file => {
      return {
        fileId: file.id || "",
        fileName: file.name || "",
      };
    });
    client
      .query<
        BulkUploadFileValidationQueryResult,
        BulkUploadFileValidationQueryVariables
      >({
        query: BulkUploadFileValidationQuery,
        variables: {
          data: { partFiles },
        },
      })
      .then(result => {
        setHasValidExtensions(
          result.data.validateBulkUploadFiles?.status === "ok"
        );
      });
  }, [files.length]);

  // Disable ability to move on from file upload page if files are missing OR there are no valid exts.
  useEffect(() => {
    setDisableFileUploadNext(() => isMissingFiles || !hasValidExtensions);
  }, [isMissingFiles, hasValidExtensions]);

  useEffect(() => {
    setDisableFileUploadNextMessage(() => {
      if (isMissingFiles) {
        return "Upload at least one file to continue.";
      }
      if (!hasValidExtensions) {
        return "Upload one file with a valid extension to continue.";
      }
      return "Something went wrong.";
    });
  }, [isMissingFiles]);

  const submitFunction = async (submissionData: SubmissionData) => {
    const processedFiles = await processFiles();
    const partFiles = processedFiles.map(file => {
      return {
        fileId: file.fileId || "",
        fileName: file.fileName || "",
      };
    });
    const bulkUploadData: BulkUploadData = {
      customerEmail: userEmail || "",
      creationTime: new Date().toJSON(),
      partFiles,
      onBehalfOfUser: onBehalfOfUser || null,
      ...submissionData,
    };
    const { data, errors } = await client.mutate<
      CustomerQuoteRequestFormMutationResult,
      CustomerQuoteRequestFormMutationVariables
    >({
      mutation: CustomerQuoteRequestFormQuery,
      variables: {
        data: bulkUploadData,
      },
    });
    if (errors) {
      return false;
    }
    // If the submission was successful, return the data (a call is successful if the salesforceCaseId is returned)
    if (data?.submitBulkUpload?.salesforceCaseId) {
      clearFiles();
      return true;
    }
    return false;
  };

  return (
    <>
      <div id="FormModal" />
      <Modal
        isOpen={isOpen}
        selector="#FormModal"
        css={FormModalCss}
        breakpointMaxWidth="80vw"
      >
        <FormStepper
          templateSlug="customer_quote_request"
          onSubmit={submissionData => {
            // FormStepper awaits onSubmit to return a truthy value to indicate success
            return submitFunction(submissionData);
          }}
          onClose={handleClose}
          onCancel={handleClose}
          successStep={{
            title: "Request Sent",
            content: <SuccessStep onClose={handleClose} />,
          }}
          fileUploadStep={{
            content: (
              <FormStepperUploadWithAnonymousUserFlow
                userEmail={userEmail}
                onSuccess={refetchUser}
              />
            ),
            stepNumber: 1,
            errorMessage:
              "Include a file from our list of approved file types to continue.",
          }}
          disableFileUploadNext={disableFileUploadNext}
          disableFileUploadNextMessage={disableFileUploadNextMessage}
          disableWhileSubmitting
          isSubmittingButtonContent={
            <>
              <i
                className="fas fa-spinner fa-spin"
                style={{ marginRight: "4px" }}
              />
              Sending Request...
            </>
          }
        />
      </Modal>
    </>
  );
};
